import React from 'react';
import PropTypes from 'prop-types';

import { t } from '../../../../i18n';
import { parseSources } from './hybridActivitySources';

import s from './HybridActivityPointCard.module.scss';

/**
 * The panel describing one map point, previewed on hover and pinned on click.
 *
 * Dressed the way the shared map's company popup is — a label over the title,
 * the glyph in a badge to their right, sources in the white strip along the
 * bottom — so both maps' cards read as the one thing. The label and the badge
 * come off the point's layer: the label names it the way "Holding" names a
 * company, and the badge wears the layer's glyph in the layer's colour.
 *
 * No close button, on a pinned card either: a click anywhere else on the map
 * puts it down, and a cross beside the badge crowded it.
 *
 * Only the pinned card carries the point's sources: a card that vanishes when
 * the pointer leaves cannot hold a clickable link. The analyst's status notes
 * are never shown — they are context for a report, not for a map.
 */
const HybridActivityPointCard = ({ properties, layer, isPinned, footer }) => {
    const { type, info, location, country, date } = properties || {};
    const sources = isPinned ? parseSources(properties?.sources) : [];

    return (
        <div className={s.Card}>
            <div className={s.Content}>
                <div className={s.Header}>
                    <div className={s.HeaderText}>
                        {layer?.name && (
                            <span className={s.Label}>{layer.name}</span>
                        )}
                        <h3 className={s.Title}>{type}</h3>
                    </div>

                    {layer?.icon && (
                        <span className={s.IconBadge}>
                            {/* Decorative — the label already names the
                                layer. The glyph is CSS content rather than
                                child text, so the symbol's name never reads
                                as part of the card. */}
                            <span
                                aria-hidden="true"
                                className={s.IconGlyph}
                                data-icon={layer.icon}
                                style={{ color: layer.colour }}
                            />
                        </span>
                    )}
                </div>

                {info && <p className={s.Body}>{info}</p>}

                <p className={s.Footer} data-testid="point-card-footer">
                    {location && (
                        // Truncated by CSS to one line; the full text stays
                        // readable on hover, since a location can run to a
                        // sentence.
                        <span className={s.Location} title={location}>
                            {location}
                        </span>
                    )}
                    {/* Written out ("Finland") on land, free text at sea
                        ("Baltic Sea") — shown as the dataset writes it. */}
                    {country && <span>{country}</span>}
                    {/* Undated by design for an asset layer, so simply absent. */}
                    {date && <span>{date}</span>}
                </p>
            </div>

            {sources.length > 0 && (
                <div className={s.Sources}>
                    <span className={s.SourcesLabel}>
                        {t('watchtowerCountryPage.hybridActivityCardSources')}
                    </span>
                    <ul className={s.SourceList}>
                        {sources.map((source) => (
                            <li key={source.url}>
                                <a
                                    className={s.Source}
                                    href={source.url}
                                    target="_blank"
                                    rel="noopener noreferrer">
                                    {source.label}
                                </a>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {footer}
        </div>
    );
};

HybridActivityPointCard.propTypes = {
    // The dataset row behind the point, straight off the GeoJSON feature.
    properties: PropTypes.shape({
        type: PropTypes.string,
        info: PropTypes.string,
        location: PropTypes.string,
        country: PropTypes.string,
        date: PropTypes.string,
        sources: PropTypes.string,
    }),
    // The configured layer the point belongs to, as the CMS authored it.
    layer: PropTypes.shape({
        name: PropTypes.string,
        icon: PropTypes.string,
        colour: PropTypes.string,
    }),
    isPinned: PropTypes.bool,
    // A strip along the card's bottom edge, inside its chrome — the cluster
    // carousel keeps its paging controls there.
    footer: PropTypes.node,
};

export default HybridActivityPointCard;
